import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ENTER } from '@angular/cdk/keycodes';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { SharedService } from '../../../services/shared.service';
import { YoutubeSourceRequest, YoutubeSourceResponse, YoutubeSourceService } from '../../../services/youtube-source.service';

interface DialogData {
	source?: YoutubeSourceResponse;
}

function toChips(commaSeparated: string | null | undefined): string[] {
	return (commaSeparated ?? '')
		.split(',')
		.map(value => value.trim())
		.filter(value => !!value);
}

@Component({
	selector: 'app-youtube-source-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatButtonModule,
		MatFormFieldModule,
		MatInputModule,
		MatCheckboxModule,
		MatChipsModule,
		MatIconModule,
		MatDividerModule,
		TranslateModule,
	],
	templateUrl: './youtube-source-dialog.component.html',
	styleUrl: './youtube-source-dialog.component.scss',
})
export class YoutubeSourceDialogComponent {
	form: FormGroup;
	saving = false;
	isEdit = false;
	readonly separatorKeyCodes: number[] = [ENTER];

	keywords: string[] = [];
	scheduleTimes: string[] = [];

	constructor(
		private fb: FormBuilder,
		private dialogRef: MatDialogRef<YoutubeSourceDialogComponent>,
		private apiYoutubeSource: YoutubeSourceService,
		private sharedService: SharedService,
		private translate: TranslateService,
		@Inject(MAT_DIALOG_DATA) public data: DialogData
	) {
		this.isEdit = !!data.source;
		this.keywords = toChips(data.source?.keywords);
		this.scheduleTimes = toChips(data.source?.customScheduleTimes);

		this.form = this.fb.group({
			name: [data.source?.name ?? '', [Validators.required]],
			url: [data.source?.url ?? '', [Validators.required]],
			enabled: [data.source?.enabled ?? true],
		});
	}

	addKeyword(event: MatChipInputEvent): void {
		const value = (event.value || '').trim();
		if (value && !this.keywords.includes(value)) {
			this.keywords.push(value);
		}
		event.chipInput.clear();
	}

	removeKeyword(value: string): void {
		this.keywords = this.keywords.filter(item => item !== value);
	}

	addScheduleTime(event: MatChipInputEvent): void {
		const value = (event.value || '').trim();
		if (value && !this.scheduleTimes.includes(value)) {
			this.scheduleTimes.push(value);
		}
		event.chipInput.clear();
	}

	removeScheduleTime(value: string): void {
		this.scheduleTimes = this.scheduleTimes.filter(item => item !== value);
	}

	save(): void {
		if (this.form.invalid || this.saving) return;
		this.saving = true;

		const body: YoutubeSourceRequest = {
			name: this.form.value.name.trim(),
			url: this.form.value.url.trim(),
			keywords: this.keywords.length ? this.keywords.join(',') : null,
			enabled: this.form.value.enabled,
			customScheduleTimes: this.scheduleTimes.length ? this.scheduleTimes.join(',') : null,
		};

		const request$ = this.isEdit && this.data.source?.id
			? this.apiYoutubeSource.updateSource(this.data.source.id, body)
			: this.apiYoutubeSource.createSource(body);

		request$.pipe(take(1)).subscribe({
			next: response => {
				this.sharedService.showNotification.next({
					message: this.translate.instant(this.isEdit ? 'NOTIFICATIONS.RECORD_UPDATED' : 'NOTIFICATIONS.RECORD_CREATED'),
					type: 'SUCCESS',
				});
				this.dialogRef.close(response);
			},
			error: err => {
				this.saving = false;
				this.sharedService.showNotification.next({
					message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err?.error?.message ?? ''}`,
					type: 'ERROR',
				});
			},
		});
	}

	cancel(): void {
		this.dialogRef.close();
	}
}
